import axiosInstance from '../../services/axiosInstance';
import type { ICategory } from '../../types/damTypes';

import type {
  LegacyRecipeListItem,
  LegacyRecipeMatchSuggestion,
  WordPressAssetStatus,
  WordPressBulkActionResult,
  WordPressFieldCheckListFilter,
  WordPressFieldCheckSettingsDetail,
  WordPressFieldCheckTargetRules,
  WordPressPublicationItem,
  WordPressQuoteBackgroundAdminItem,
  WordPressQuoteBackgroundAssetSummary,
  WordPressResetResult,
  WordPressResetTarget,
  WordPressWineryImageAdminItem,
  WordPressWineryImageAssetSummary,
  WordPressPublicationType,
  WordPressPublishingSettingsDetail,
  WordPressPublishingSummary,
  WordPressRecipeCrop,
  WordPressSyncWarningGroupsResult,
} from './types';

type ItemQueryParams = {
  type: WordPressPublicationType;
  search?: string;
  status?: string;
  fieldCheck?: WordPressFieldCheckListFilter;
  syncWarnings?: 'only';
  onlyUnsynced?: boolean;
  onlyWithoutRendition?: boolean;
  onlyNotApproved?: boolean;
  limit?: number;
  offset?: number;
};

export async function fetchWordPressPublishingSummary(): Promise<WordPressPublishingSummary> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/summary');
  return data.summary;
}

export async function fetchWordPressPublishingSettings(): Promise<WordPressPublishingSettingsDetail> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/settings');
  return {
    settings: data.settings,
    resolutions: data.resolutions,
  };
}

export async function saveWordPressPublishingSettings(
  categoryRoots: Record<WordPressPublicationType, string | null>,
): Promise<WordPressPublishingSettingsDetail> {
  const { data } = await axiosInstance.put('/api/wordpress-publishing/settings', {
    categoryRoots,
  });

  return {
    settings: data.settings,
    resolutions: data.resolutions,
  };
}

export async function fetchWordPressFieldCheckSettings(): Promise<WordPressFieldCheckSettingsDetail> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/field-checks');
  return {
    settings: data.settings,
    catalogs: data.catalogs,
  };
}

export async function saveWordPressFieldCheckTargetRules(
  type: WordPressPublicationType,
  rules: WordPressFieldCheckTargetRules,
): Promise<WordPressFieldCheckSettingsDetail> {
  const { data } = await axiosInstance.put(`/api/wordpress-publishing/field-checks/${type}`, rules);
  return {
    settings: data.settings,
    catalogs: data.catalogs,
  };
}

export async function fetchWordPressRootCategories(): Promise<ICategory[]> {
  const { data } = await axiosInstance.get('/api/categories/main');
  return Array.isArray(data) ? data : [];
}

export async function fetchWordPressPublicationItems(params: ItemQueryParams): Promise<{
  items: WordPressPublicationItem[];
  total: number;
}> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/items', {
    params,
  });
  return {
    items: data.items || [],
    total: Number(data.total || 0),
  };
}

export async function fetchWordPressSyncWarningGroups(target?: WordPressPublicationType | null): Promise<WordPressSyncWarningGroupsResult> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/sync-warnings', {
    params: target ? { target } : undefined,
  });
  return {
    groups: data.groups || [],
    publicationCount: Number(data.publicationCount || 0),
    assetCount: Number(data.assetCount || 0),
  };
}

export async function fetchWordPressPublicationItem(assetId: string, type?: WordPressPublicationType | null): Promise<WordPressPublicationItem | null> {
  const { data } = await axiosInstance.get(`/api/wordpress-publishing/items/${assetId}`, {
    params: type ? { type } : undefined,
  });
  return data.item || null;
}

export async function fetchWordPressAssetStatus(assetId: string): Promise<WordPressAssetStatus | null> {
  const { data } = await axiosInstance.get(`/api/wordpress-publishing/status/${assetId}`);
  return data.status || null;
}

export async function setWordPressPublicationEnabled(assetId: string, type: WordPressPublicationType, enabled: boolean): Promise<WordPressPublicationItem | null> {
  const { data } = await axiosInstance.post(`/api/wordpress-publishing/items/${assetId}/enable`, {
    type,
    enabled,
  });
  return data.item || null;
}

export async function generateWordPressPublicationImage(
  assetId: string,
  type: WordPressPublicationType,
  recipeCrop?: WordPressRecipeCrop | null,
): Promise<WordPressPublicationItem | null> {
  const { data } = await axiosInstance.post(`/api/wordpress-publishing/items/${assetId}/generate`, {
    type,
    recipeCrop,
  });
  return data.item || null;
}

export async function approveWordPressPublication(assetId: string, type: WordPressPublicationType, approved = true): Promise<WordPressPublicationItem | null> {
  const { data } = await axiosInstance.post(`/api/wordpress-publishing/items/${assetId}/approve`, {
    type,
    approved,
  });
  return data.item || null;
}

export async function requestWordPressPublicationSync(assetId: string, type: WordPressPublicationType): Promise<WordPressPublicationItem | null> {
  const { data } = await axiosInstance.post(`/api/wordpress-publishing/items/${assetId}/request-sync`, {
    type,
  });
  return data.item || null;
}

export async function requestWordPressPublicationUnpublish(assetId: string, type: WordPressPublicationType): Promise<WordPressPublicationItem | null> {
  const { data } = await axiosInstance.post(`/api/wordpress-publishing/items/${assetId}/unpublish`, {
    type,
  });
  return data.item || null;
}

export async function runWordPressBulkAction(params: {
  assetIds: string[];
  type: WordPressPublicationType;
  action: 'enable' | 'generate' | 'approve' | 'request-sync' | 'unpublish' | 'reset';
}): Promise<WordPressBulkActionResult> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/items/bulk/action', params);
  return data.result;
}

export const WORDPRESS_PUBLISHING_RESET_CONFIRMATION = 'NOLLSTALL_WORDPRESS_PUBLICERING';

export async function resetWordPressPublications(params: {
  target: WordPressResetTarget;
  confirmation: string;
}): Promise<WordPressResetResult> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/reset', params);
  return data.result;
}

export const LEGACY_MATCHING_LIST_LIMIT = 250;
export const MANUAL_DAM_SEARCH_LIMIT = 50;

export async function fetchLegacyRecipeItems(search: string, onlyUnlinked: boolean): Promise<{
  items: LegacyRecipeListItem[];
  total: number;
}> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/matching/legacy-recipes', {
    params: {
      search,
      onlyUnlinked,
      limit: LEGACY_MATCHING_LIST_LIMIT,
    },
  });

  return {
    items: data.items || [],
    total: Number(data.total || 0),
  };
}

export async function fetchLegacyRecipeSuggestions(legacyAssetId: string): Promise<LegacyRecipeMatchSuggestion[]> {
  const { data } = await axiosInstance.get(`/api/wordpress-publishing/matching/${legacyAssetId}/suggestions`);
  return data.suggestions || [];
}

export async function searchLegacyRecipeSourceAssets(
  legacyAssetId: string,
  search: string,
  limit = MANUAL_DAM_SEARCH_LIMIT,
): Promise<LegacyRecipeMatchSuggestion['asset'][]> {
  const { data } = await axiosInstance.get(`/api/wordpress-publishing/matching/${legacyAssetId}/assets/search`, {
    params: {
      search,
      limit,
    },
  });
  return data.assets || [];
}

export async function linkLegacyRecipeToSource(
  sourceAssetId: string,
  legacyAssetId: string,
  preserveOriginal = false,
): Promise<void> {
  await axiosInstance.post('/api/wordpress-publishing/matching/link', {
    sourceAssetId,
    legacyAssetId,
    preserveOriginal,
  });
}

export async function reindexLegacyRecipeMatching(): Promise<void> {
  await axiosInstance.post('/api/wordpress-publishing/matching/reindex');
}

export async function fetchWordPressQuoteBackgrounds(): Promise<{
  fieldId: string;
  fieldLabel: string;
  fallbackProductCount: number;
  items: WordPressQuoteBackgroundAdminItem[];
}> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/quote-backgrounds');
  return {
    fieldId: data.fieldId,
    fieldLabel: data.fieldLabel,
    fallbackProductCount: Number(data.fallbackProductCount || 0),
    items: data.items || [],
  };
}

export async function searchWordPressQuoteBackgroundAssets(search: string, limit = 40): Promise<WordPressQuoteBackgroundAssetSummary[]> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/quote-backgrounds/assets/search', {
    params: {
      search,
      limit,
    },
  });
  return data.assets || [];
}

export async function saveWordPressQuoteBackground(params: {
  key: string;
  label?: string;
  isFallback?: boolean;
  sourceAssetId?: string | null;
  altText?: string;
  title?: string;
  description?: string;
}): Promise<WordPressQuoteBackgroundAdminItem | null> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/quote-backgrounds/save', params);
  return data.item || null;
}

export async function generateWordPressQuoteBackground(params: {
  key: string;
  label?: string;
  isFallback?: boolean;
  sourceAssetId?: string | null;
  crop?: WordPressRecipeCrop | null;
  altText?: string;
  title?: string;
  description?: string;
}): Promise<WordPressQuoteBackgroundAdminItem | null> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/quote-backgrounds/generate', params);
  return data.item || null;
}

export async function clearWordPressQuoteBackground(key: string): Promise<WordPressQuoteBackgroundAdminItem | null> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/quote-backgrounds/clear', { key });
  return data.item || null;
}

export async function fetchWordPressWineryImages(): Promise<{
  producerFieldId: string;
  fieldId: string;
  fieldLabel: string;
  fallbackWineCount: number;
  items: WordPressWineryImageAdminItem[];
}> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/winery-images');
  return {
    producerFieldId: data.producerFieldId,
    fieldId: data.fieldId,
    fieldLabel: data.fieldLabel,
    fallbackWineCount: Number(data.fallbackWineCount || 0),
    items: data.items || [],
  };
}

export async function searchWordPressWineryImageAssets(search: string, limit = 40): Promise<WordPressWineryImageAssetSummary[]> {
  const { data } = await axiosInstance.get('/api/wordpress-publishing/winery-images/assets/search', {
    params: {
      search,
      limit,
    },
  });
  return data.assets || [];
}

export async function saveWordPressWineryImage(params: {
  key: string;
  label?: string;
  isFallback?: boolean;
  sourceAssetId?: string | null;
  altText?: string;
  title?: string;
  description?: string;
  wineryDescription?: string;
}): Promise<WordPressWineryImageAdminItem | null> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/winery-images/save', params);
  return data.item || null;
}

export async function generateWordPressWineryImage(params: {
  key: string;
  label?: string;
  isFallback?: boolean;
  sourceAssetId?: string | null;
  crop?: WordPressRecipeCrop | null;
  altText?: string;
  title?: string;
  description?: string;
  wineryDescription?: string;
}): Promise<WordPressWineryImageAdminItem | null> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/winery-images/generate', params);
  return data.item || null;
}

export async function clearWordPressWineryImage(key: string): Promise<WordPressWineryImageAdminItem | null> {
  const { data } = await axiosInstance.post('/api/wordpress-publishing/winery-images/clear', { key });
  return data.item || null;
}
